import { useCallback, useState } from 'react';
import useFetch, { ServiceParams, ServiceResponse } from '../hooks/useFetch';
import EndPoints from '../config/EndPoint';

export type AdicionalEvents = Partial<{
  onSuccess: (r: ServiceResponse) => void;
  onError: (r: ServiceResponse) => void;
}>;

export interface ManagementItem {
  id: number;
  name: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface SuperintendencyItem {
  id: number;
  name: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface AreaItem {
  id: number;
  name: string;
  area_responsible: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ServiceReasonItem {
  id: number;
  description: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ElAbraGroupItem {
  id: number;
  name: string;
  display_name: string;
  management: number;
  management_name: string;
  superintendency: number;
  superintendency_name: string;
  area: number;
  area_name: string;
  area_responsible: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

const catalogListQs = (page: number, pageSize: number, search?: string) => {
  const p = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  const s = search?.trim();
  if (s) p.set('search', s);
  return p.toString();
};

const useAdicionalCatalogService = () => {
  const { doGet, doPost, doPut } = useFetch();
  const [fetching, setFetching] = useState<Record<string, boolean>>({});

  const setFetch = useCallback((key: string, val: boolean) => {
    setFetching(prev => ({ ...prev, [key]: val }));
  }, []);

  const listManagements = useCallback(
    (page = 1, events: AdicionalEvents = {}, search?: string, pageSize = 200) =>
      doGet({
        url: `${EndPoints.MANAGEMENTS.LIST}?${catalogListQs(page, pageSize, search)}`,
        setFetching: v => setFetch('managements', v),
        ...events,
      } as ServiceParams),
    [doGet, setFetch]
  );

  const storeManagement = useCallback(
    (payload: { name: string }, events: AdicionalEvents = {}) =>
      doPost({
        url: EndPoints.MANAGEMENTS.STORE,
        body: payload,
        setFetching: v => setFetch('storeManagement', v),
        ...events,
      } as ServiceParams),
    [doPost, setFetch]
  );

  const listSuperintendencies = useCallback(
    (page = 1, events: AdicionalEvents = {}, search?: string, pageSize = 200) =>
      doGet({
        url: `${EndPoints.SUPERINTENDENCIES.LIST}?${catalogListQs(page, pageSize, search)}`,
        setFetching: v => setFetch('superintendencies', v),
        ...events,
      } as ServiceParams),
    [doGet, setFetch]
  );

  const storeSuperintendency = useCallback(
    (payload: { name: string }, events: AdicionalEvents = {}) =>
      doPost({
        url: EndPoints.SUPERINTENDENCIES.STORE,
        body: payload,
        setFetching: v => setFetch('storeSuperintendency', v),
        ...events,
      } as ServiceParams),
    [doPost, setFetch]
  );

  const listAreas = useCallback(
    (page = 1, events: AdicionalEvents = {}, search?: string, pageSize = 200) =>
      doGet({
        url: `${EndPoints.AREAS.LIST}?${catalogListQs(page, pageSize, search)}`,
        setFetching: v => setFetch('areas', v),
        ...events,
      } as ServiceParams),
    [doGet, setFetch]
  );

  const storeArea = useCallback(
    (payload: { name: string; area_responsible?: string }, events: AdicionalEvents = {}) =>
      doPost({
        url: EndPoints.AREAS.STORE,
        body: payload,
        setFetching: v => setFetch('storeArea', v),
        ...events,
      } as ServiceParams),
    [doPost, setFetch]
  );

  const listServiceReasons = useCallback(
    (page = 1, events: AdicionalEvents = {}, search?: string) =>
      doGet({
        url: `${EndPoints.SERVICE_REASONS.LIST}?${catalogListQs(page, 200, search)}`,
        setFetching: v => setFetch('serviceReasons', v),
        ...events,
      } as ServiceParams),
    [doGet, setFetch]
  );

  const storeServiceReason = useCallback(
    (payload: { description: string }, events: AdicionalEvents = {}) =>
      doPost({
        url: EndPoints.SERVICE_REASONS.STORE,
        body: payload,
        setFetching: v => setFetch('storeServiceReason', v),
        ...events,
      } as ServiceParams),
    [doPost, setFetch]
  );

  const listElAbraGroups = useCallback(
    (page = 1, events: AdicionalEvents = {}, search?: string, pageSize = 200) =>
      doGet({
        url: `${EndPoints.EL_ABRA_GROUPS.LIST}?${catalogListQs(page, pageSize, search)}`,
        setFetching: v => setFetch('elAbraGroups', v),
        ...events,
      } as ServiceParams),
    [doGet, setFetch]
  );

  const storeElAbraGroup = useCallback(
    (
      payload: {
        name?: string;
        management: number;
        superintendency: number;
        area: number;
      },
      events: AdicionalEvents = {}
    ) =>
      doPost({
        url: EndPoints.EL_ABRA_GROUPS.STORE,
        body: payload,
        setFetching: v => setFetch('storeElAbraGroup', v),
        ...events,
      } as ServiceParams),
    [doPost, setFetch]
  );

  const updateElAbraGroup = useCallback(
    (
      id: number,
      payload: {
        management: number;
        superintendency: number;
        area: number;
      },
      events: AdicionalEvents = {}
    ) =>
      doPut({
        url: EndPoints.EL_ABRA_GROUPS.UPDATE.replace(":id", String(id)),
        body: payload,
        setFetching: v => setFetch('updateElAbraGroup', v),
        ...events,
      } as ServiceParams),
    [doPut, setFetch]
  );

  return {
    fetching,
    listManagements,
    storeManagement,
    listSuperintendencies,
    storeSuperintendency,
    listAreas,
    storeArea,
    listServiceReasons,
    storeServiceReason,
    listElAbraGroups,
    storeElAbraGroup,
    updateElAbraGroup,
  };
};

export default useAdicionalCatalogService;
